import { Component, HostListener, inject, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { takeUntil } from 'rxjs';

import {
  AssetListSortType,
  AssetListSortTypes,
  ExportOrientationType,
  ExportOrientationTypes,
  NumberItemsPerPageType,
  NumberItemsPerPageTypes,
} from '../../models';
import { AssetListService } from '../../services/asset-list.service';
import { DateHelperService } from '../../services/date-helper.service';
import { UnSubscribeComponent } from '../un-subscribe/un-subscribe.component';
import { FeatureConfigService } from '../../services/feature-config.service';
import { CampaignAssetListFeatureConfig, FeatureTypes } from '../../interfaces';
import { LogService } from '../../services/log.service';
import { AssetExportFilterService, AssetExportFilterState } from './services/asset-export-filter.service';
import { AssetExportSorterService } from './services/asset-export-sorter.service';
import { AssetExportDataService } from './services/asset-export-data.service';
import { AssetExportHelperService } from './services/asset-export-helper.service';
import { AssetExportOperationsService } from './services/asset-export-operations.service';

type ExportType = 'PDF' | 'Excel' | 'CSV';

@Component({
  selector: 'mtm-asset-export-modal',
  templateUrl: './asset-export-modal.component.html',
  styleUrl: './asset-export-modal.component.scss',
  standalone: false,
  providers: [
    AssetExportFilterService,
    AssetExportSorterService,
    AssetExportDataService,
    AssetExportHelperService,
    AssetExportOperationsService,
  ],
})
export class AssetExportModalComponent extends UnSubscribeComponent implements OnInit {
  private readonly assetListService: AssetListService = inject(AssetListService);
  private readonly featureConfigService: FeatureConfigService = inject(FeatureConfigService);
  private readonly dateHelperService: DateHelperService = inject(DateHelperService);
  private readonly logService: LogService = inject(LogService);
  private readonly filterService: AssetExportFilterService = inject(AssetExportFilterService);
  private readonly sorterService: AssetExportSorterService = inject(AssetExportSorterService);
  private readonly dataService: AssetExportDataService = inject(AssetExportDataService);
  private readonly helperService: AssetExportHelperService = inject(AssetExportHelperService);
  private readonly operationsService: AssetExportOperationsService = inject(AssetExportOperationsService);

  currentSort: AssetListSortTypes = AssetListSortType.releaseDate;
  orientation: ExportOrientationTypes = ExportOrientationType.landscape;
  numberItemsPerPage: NumberItemsPerPageTypes = NumberItemsPerPageType.landscape;
  totalPages: number = 1;
  portraitSize: number[] = [656, 928];
  landscapeSize: number[] = [1094, 583];

  assets: any[] = [];
  originalAssets: any[] = [];
  campaignId: string;
  flattenAssets: any[] = [];
  assetsPerPages: any[][] = [];
  isLoading: boolean = false;
  selectedProjectIds: string[] = [];
  selectedFocusValues: string[] = [];
  selectedTypeValues: string[] = [];
  selectedBetsValues: string[] = [];
  selectedDurationValues: string[] = [];
  selectedFormatValues: string[] = [];
  selectedCategoriesValues: string[] = [];
  selectedInUseValues: string[] = [];
  selectedStatusValues: string[] = [];
  selectedReleaseDateValues: string[] = [];
  showHiddenOnly: boolean = false;
  affectedAssets: Record<string, any> = {};
  showRegions: boolean = false;
  showAgency: boolean = false;
  showFocus: boolean = false;
  showLength: boolean = false;
  showTypes: boolean = false;
  showFormat: boolean = false;
  loaderVisible: boolean = false;

  showExportTypeModal: boolean = false;
  exportTypes: ExportType[] = ['PDF', 'Excel', 'CSV'];
  selectedExportTypes: ExportType[] = ['PDF'];

  constructor(public activeModal: NgbActiveModal) {
    super();
  }

  ngOnInit(): void {
    this.checkFeatureConfig();
    this.originalAssets = structuredClone(this.assets);
    this.loadAffectedAssets();
    this.handleData();
  }

  private checkFeatureConfig(): void {
    this.featureConfigService
      .getTypedFeatureConfig(FeatureTypes.FEA_CAMPAIGN_ASSETLIST)
      .pipe(takeUntil(this.unSubscribeOnDestroy))
      .subscribe({
        next: (config: CampaignAssetListFeatureConfig) => {
          if (config) {
            this.showRegions = !!config.region?.length;
            this.showAgency = !!config.agency?.length;
            this.showFocus = !!config.focus?.length;
            this.showLength = !!config.length?.length;
            this.showTypes = !!config.type?.length;
            this.showFormat = !!config.format?.length;
          }
        },
      });
  }

  private loadAffectedAssets(): void {
    this.assetListService
      .getAffectedAssetByCampaignId(this.campaignId)
      .pipe(takeUntil(this.unSubscribeOnDestroy))
      .subscribe({
        next: (res: { assetListId: string; deliveryDate?: string }[]) => {
          this.affectedAssets = this.helperService.processAffectedAssets(res, this.dateHelperService);
        },
      });
  }

  public handleData(): void {
    if (!this.assets?.length) {
      return;
    }
    const sorted: any[] = this.sorterService.sortAssets(this.assets, this.currentSort, this.affectedAssets);
    const filtered: any[] = this.filterAssets(sorted);
    this.flattenAssets = this.dataService.flattenAssets(filtered);
    this.totalPages = this.helperService.calculateTotalPages(this.flattenAssets.length, this.numberItemsPerPage);
    this.assetsPerPages = this.helperService.paginateAssets(this.flattenAssets, this.numberItemsPerPage);
  }

  private filterAssets(assets: any[]): any[] {
    const filterState: AssetExportFilterState = {
      selectedProjectIds: this.selectedProjectIds,
      selectedFocusValues: this.selectedFocusValues,
      selectedTypeValues: this.selectedTypeValues,
      selectedBetsValues: this.selectedBetsValues,
      selectedDurationValues: this.selectedDurationValues,
      selectedFormatValues: this.selectedFormatValues,
      selectedCategoriesValues: this.selectedCategoriesValues,
      selectedInUseValues: this.selectedInUseValues,
      selectedStatusValues: this.selectedStatusValues,
      selectedReleaseDateValues: this.selectedReleaseDateValues,
      showHiddenOnly: this.showHiddenOnly,
    };
    return this.filterService.filterAssets(assets, filterState, this.affectedAssets);
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    const exportBtnElement: Element | null = (event.target as HTMLElement).closest('.export-btn');
    const exportModalElement: Element | null = (event.target as HTMLElement).closest('.export-type-modal');
    if (!exportBtnElement && !exportModalElement && this.showExportTypeModal) {
      this.showExportTypeModal = false;
    }
  }

  public onExportClick(event: MouseEvent): void {
    event.stopPropagation();
    this.showExportTypeModal = !this.showExportTypeModal;
  }

  public onExportTypeSelect(type: ExportType): void {
    if (this.selectedExportTypes.includes(type)) {
      this.selectedExportTypes = this.selectedExportTypes.filter((t: ExportType) => t !== type);
    } else {
      this.selectedExportTypes.push(type);
    }
  }

  public async onExport(): Promise<void> {
    this.showExportTypeModal = false;
    this.loaderVisible = true;
    try {
      if (this.selectedExportTypes.includes('PDF')) {
        await this.exportToPDF();
      }
      if (this.selectedExportTypes.includes('Excel')) {
        await this.exportToSheet();
      }
      if (this.selectedExportTypes.includes('CSV')) {
        await this.exportToCSV();
      }
    } catch (error) {
      this.logService.logError(error, 'Failed to export Asset List: ' + (error as { message?: string })?.message);
    } finally {
      this.loaderVisible = false;
    }
  }

  private async exportToPDF(): Promise<void> {
    this.isLoading = true;
    try {
      const pdfData: any[] = this.dataService.getPdfData(this.flattenAssets);
      const displayFlags: {
        showRegions: boolean;
        showAgency: boolean;
        showFocus: boolean;
        showLength: boolean;
        showTypes: boolean;
        showFormat: boolean;
      } = {
        showRegions: this.showRegions,
        showAgency: this.showAgency,
        showFocus: this.showFocus,
        showLength: this.showLength,
        showTypes: this.showTypes,
        showFormat: this.showFormat,
      };
      await this.operationsService.exportToPDF(pdfData, displayFlags, () => {
        this.isLoading = false;
      });
    } catch (error) {
      this.logService.logError(error, 'Failed to export Asset List PDF: ' + (error as { message?: string })?.message);
      this.isLoading = false;
    }
  }

  private async exportToSheet(): Promise<void> {
    try {
      this.isLoading = true;
      const exportData: Record<string, string>[] = this.dataService.getExportData(this.flattenAssets);
      const sheetData: string[][] = this.dataService.convertToSheetData(exportData);
      await this.operationsService.exportToExcel(
        sheetData,
        () => {
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
        }
      );
    } catch (error) {
      this.logService.logMessage('error', 'Export Asset List Excel:' + (error as { message?: string })?.message, error);
      this.isLoading = false;
    }
  }

  private async exportToCSV(): Promise<void> {
    try {
      this.isLoading = true;
      const exportData: Record<string, string>[] = this.dataService.getExportData(this.flattenAssets);
      const csvContent: string = this.dataService.convertToCsvContent(exportData);
      await this.operationsService.exportToCSV(csvContent);
    } catch (error: any) {
      this.logService.logError(error as Error, 'CSV export error: ' + (error as { message?: string })?.message);
    } finally {
      this.isLoading = false;
    }
  }

  public onCancel(): void {
    this.activeModal.dismiss();
  }

  public onOrientationChange(orientation: ExportOrientationTypes): void {
    switch (orientation) {
      case ExportOrientationType.landscape:
        this.numberItemsPerPage = NumberItemsPerPageType.landscape;
        break;
      case ExportOrientationType.portrait:
        this.numberItemsPerPage = NumberItemsPerPageType.portrait;
        break;
    }
    this.orientation = orientation;
    this.handleData();
  }

  public onSortChange(sort: AssetListSortTypes): void {
    this.currentSort = sort;
    this.handleData();
  }

  public onSelectedProjectIdsChange(value: string[]): void {
    this.selectedProjectIds = [...value];
    this.handleData();
  }

  public onSelectedFocusValuesChange(value: string[]): void {
    this.selectedFocusValues = [...value];
    this.handleData();
  }

  public onSelectedTypeValuesChange(value: string[]): void {
    this.selectedTypeValues = [...value];
    this.handleData();
  }

  public onSelectedBetsValuesChange(value: string[]): void {
    this.selectedBetsValues = [...value];
    this.handleData();
  }

  public onSelectedDurationValuesChange(value: string[]): void {
    this.selectedDurationValues = [...value];
    this.handleData();
  }

  public onSelectedFormatValuesChange(value: string[]): void {
    this.selectedFormatValues = [...value];
    this.handleData();
  }

  public onSelectedCategoriesValuesChange(value: string[]): void {
    this.selectedCategoriesValues = [...value];
    this.handleData();
  }

  public onSelectedInUseValuesChange(value: string[]): void {
    this.selectedInUseValues = [...value];
    this.handleData();
  }

  public onSelectedStatusValuesChange(value: string[]): void {
    this.selectedStatusValues = [...value];
    this.handleData();
  }

  public onShowHiddenOnlyChange(value: boolean): void {
    this.showHiddenOnly = value;
    this.handleData();
  }

  public onSelectedReleaseDateValuesChange(value: string[]): void {
    this.selectedReleaseDateValues = [...value];
    this.handleData();
  }
}
